/* eslint-disable @typescript-eslint/ban-types */
import { accountableAPI } from 'config';
import type { QueryClient } from 'react-query';
import { AccountCacheKeys, WorkspaceCacheKeys } from 'types/cacheKeys.types';
import type {
  AccountantAuthenticationDataType,
  IAPIRefreshAccessTokenResponse,
} from 'types/auth.types';
import { deparallelize } from './promise';

/**
 * Expert Authentication Utilities
 * Separate from web auth to handle different data structures
 * Expert users have nested structure: { accountant: {...}, auth: { access_token, ... } }
 */

export function getStoredExpertAuthData(): AccountantAuthenticationDataType;

export function getStoredExpertAuthData<
  K extends keyof AccountantAuthenticationDataType,
>(key: K): AccountantAuthenticationDataType[K] | undefined;

export function getStoredExpertAuthData(
  key?: string,
): AccountantAuthenticationDataType | {} | string | undefined {
  const authData =
    window.self != window.top // If in an iframe, keep logged out.
      ? {}
      : JSON.parse(localStorage.getItem('auth') || '{}');

  return key ? authData[key] : authData;
}

export const getUpToDateExpertAuthData = deparallelize(
  async (): Promise<AccountantAuthenticationDataType | null> => {
    const authData =
      getStoredExpertAuthData() as AccountantAuthenticationDataType;

    // Check for nested auth structure (expert format)
    if (!authData?.auth?.access_token) return null;

    const timeToExpiry = authData.auth.exp - Date.now() / 1000;

    // If less than 10 minutes remaining until expiry
    if (timeToExpiry < 600) {
      // Update access token in the background
      const newAuthDataPromise = forceUpdateExpertAuthData();

      // If less than 5 seconds remaining until expiry
      if (timeToExpiry < 5) {
        // Wait for the updated auth data instead
        return newAuthDataPromise;
      }
    }

    return authData;
  },
);

const baseUrl = accountableAPI.baseUrl;

/**
 * Updates expert auth data with new JWT token and syncs React Query cache
 * ONE function to handle storage + cache sync
 *
 * Usage: updateExpertAuth(newAuthFromBackend, queryClient)
 *
 * We should do it in next cases:
 * sign in/up
 * accept invitation
 * create workspace
 * switch-workspace
 * delete workspace
 * else?
 */

export const forceUpdateExpertAuthData = deparallelize(
  async (): Promise<AccountantAuthenticationDataType | null> => {
    const authData =
      getStoredExpertAuthData() as AccountantAuthenticationDataType;

    if (!authData?.auth?.refresh_token) return null;

    // If refresh token is already expired, don't bother trying to refresh access token
    if (new Date(authData.auth.refresh_token_expires_at).getTime() < Date.now())
      return null;

    const { refresh_token } = authData.auth;

    const response = await fetch(
      `${baseUrl}/v3/access/accountants/refresh-token`, // Expert-specific endpoint
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      },
    );

    if (!response.ok) return null;

    const { access_token, finalPayload }: IAPIRefreshAccessTokenResponse =
      await response.json();

    // Update the nested auth structure
    const updatedAuthData = {
      ...authData,
      auth: {
        ...authData.auth,
        access_token,
        ...finalPayload,
        exp: finalPayload.exp - finalPayload.iat + Date.now() / 1000,
      },
    };

    localStorage.setItem('auth', JSON.stringify(updatedAuthData));

    return updatedAuthData as AccountantAuthenticationDataType;
  },
);
